import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Sparkles, BookOpen } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BookAutocompleteInline from "@/components/BookAutocompleteInline";

export default function ClubCreate() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    const [form, setForm] = useState({
        name: "",
        theme: "",
        description: "",
        language: (i18n.language || "en").split("-")[0],
    });
    const [overrideBook, setOverrideBook] = useState(false);
    const [bookTitle, setBookTitle] = useState("");
    const [bookAuthor, setBookAuthor] = useState("");
    const [bookIsbn, setBookIsbn] = useState("");
    const [bookCover, setBookCover] = useState("");
    const [bookPages, setBookPages] = useState("");
    const [bookWhy, setBookWhy] = useState("");
    const [busy, setBusy] = useState(false);

    if (loading) return null;
    if (!user || typeof user !== "object") {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <p className="font-display text-2xl text-[var(--ink)] mb-3"
                   style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                    {t("clubs.login_required")}
                </p>
                <div className="flex gap-3 mt-4">
                    <Link to="/acceder" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                        {t("nav.login")}
                    </Link>
                </div>
            </main>
        );
    }

    const handle = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form };
            if (overrideBook && bookTitle.trim() && bookAuthor.trim()) {
                payload.first_book = {
                    title: bookTitle.trim(),
                    author: bookAuthor.trim(),
                    isbn: bookIsbn.trim() || null,
                    cover_url: bookCover.trim() || null,
                    pages: bookPages ? parseInt(bookPages, 10) : null,
                    why: bookWhy.trim() || null,
                };
            }
            const { data } = await api.post("/clubs", payload);
            toast.success(t("clubs.created"));
            navigate(`/clubs/${data.slug}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <Link to="/clubs" className="text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors mb-6 inline-block">
                ← {t("clubs.back_to_list")}
            </Link>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">{t("clubs.create_eyebrow")}</p>
            <h1 className="font-display text-5xl md:text-6xl text-[var(--ink)] mb-4"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}>
                {t("clubs.create_title")}
            </h1>
            <p className="text-[var(--ink-muted)] text-lg mb-12 max-w-2xl">{t("clubs.create_lead")}</p>

            <form onSubmit={handle} className="space-y-6">
                <Field label={t("clubs.field_name")}>
                    <input
                        data-testid="club-create-name"
                        required
                        minLength={2}
                        maxLength={80}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={inputCls}
                    />
                </Field>
                <Field label={t("clubs.field_theme")} hint={t("clubs.field_theme_hint")}>
                    <input
                        data-testid="club-create-theme"
                        required
                        minLength={2}
                        maxLength={160}
                        value={form.theme}
                        onChange={(e) => setForm({ ...form, theme: e.target.value })}
                        className={inputCls}
                    />
                </Field>
                <Field label={t("clubs.field_description")}>
                    <textarea
                        data-testid="club-create-description"
                        rows={3}
                        maxLength={600}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className={`${inputCls} resize-none`}
                    />
                </Field>
                <Field label={t("clubs.field_language")}>
                    <select
                        data-testid="club-create-language"
                        value={form.language}
                        onChange={(e) => setForm({ ...form, language: e.target.value })}
                        className={inputCls}
                    >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="fr">Français</option>
                        <option value="pt">Português</option>
                        <option value="de">Deutsch</option>
                        <option value="it">Italiano</option>
                    </select>
                </Field>

                {/* Optional first-book override */}
                <div className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-5 rounded-md">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="club-override-toggle"
                            checked={overrideBook}
                            onChange={(e) => setOverrideBook(e.target.checked)}
                            className="h-4 w-4 accent-[var(--accent)]"
                        />
                        <span className="text-sm text-[var(--ink)]">
                            {t("clubs.override_toggle")}
                        </span>
                    </label>
                    <p className="text-xs text-[var(--ink-dim)] mt-2 pl-7 flex items-start gap-2">
                        <Sparkles size={12} strokeWidth={1.5} className="text-[var(--accent)] mt-0.5 flex-shrink-0" />
                        {overrideBook ? t("clubs.override_hint_on") : t("clubs.override_hint_off")}
                    </p>

                    {overrideBook && (
                        <div className="mt-5 space-y-4 border-t border-[var(--border)] pt-5">
                            <div>
                                <span className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                                    {t("clubs.override_title")}
                                </span>
                                <BookAutocompleteInline
                                    value={bookTitle}
                                    onChange={setBookTitle}
                                    onPick={(item) => {
                                        setBookTitle(item.title || "");
                                        setBookAuthor(item.author || "");
                                        setBookIsbn(item.isbn || "");
                                        setBookCover(item.cover || "");
                                    }}
                                    placeholder={t("clubs.override_title_placeholder")}
                                    inputClassName={inputCls}
                                    testId="club-override-title"
                                />
                            </div>
                            <Field label={t("clubs.override_author")}>
                                <input
                                    data-testid="club-override-author"
                                    value={bookAuthor}
                                    onChange={(e) => setBookAuthor(e.target.value)}
                                    className={inputCls}
                                />
                            </Field>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label={t("clubs.override_isbn")}>
                                    <input
                                        data-testid="club-override-isbn"
                                        value={bookIsbn}
                                        onChange={(e) => setBookIsbn(e.target.value)}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label={t("clubs.override_pages")}>
                                    <input
                                        data-testid="club-override-pages"
                                        type="number"
                                        min="1"
                                        max="2000"
                                        value={bookPages}
                                        onChange={(e) => setBookPages(e.target.value)}
                                        className={inputCls}
                                    />
                                </Field>
                            </div>
                            <Field label={t("clubs.override_why")} hint={t("clubs.override_why_hint")}>
                                <textarea
                                    data-testid="club-override-why"
                                    rows={2}
                                    maxLength={600}
                                    value={bookWhy}
                                    onChange={(e) => setBookWhy(e.target.value)}
                                    className={`${inputCls} resize-none`}
                                />
                            </Field>
                            {bookCover && (
                                <div className="flex items-center gap-3">
                                    <img
                                        src={bookCover}
                                        alt=""
                                        className="w-14 h-20 object-cover border border-[var(--border-strong)]"
                                    />
                                    <p className="text-xs text-[var(--ink-dim)]">{t("clubs.override_cover_caption")}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Cycle preview */}
                <aside
                    data-testid="club-cycle-preview"
                    className="border border-[var(--border)] bg-[var(--bg)] p-5 rounded-md"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-2 flex items-center gap-2">
                        <BookOpen size={12} strokeWidth={1.5} />
                        {t("clubs.preview_title")}
                    </p>
                    <p className="text-sm text-[var(--ink-muted)]">
                        {t("clubs.preview_body")}
                    </p>
                </aside>

                <button
                    type="submit"
                    data-testid="club-create-submit"
                    disabled={busy}
                    className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                >
                    {busy && <Loader2 size={14} className="animate-spin" />}
                    {t("clubs.create_submit")}
                </button>
            </form>
        </main>
    );
}

const inputCls =
    "w-full bg-transparent border-b border-[var(--ink)] py-2 text-base text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors";

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">{label}</span>
            {children}
            {hint && <p className="text-xs text-[var(--ink-dim)] mt-1.5">{hint}</p>}
        </label>
    );
}
